/**
 * TextRenderers — plain-text and data-driven text components.
 *
 * Extracts SharedTextComponent (→ TextRenderer) and SharedDataTextComponent
 * (→ DataTextRenderer) from SharedComponentRenderer.tsx.
 *
 * Step 10 of the decomposition roadmap.
 *
 * Violations fixed in this file:
 *
 * Group C — unsafe annotation casts
 *   • SharedTextComponent.getResponsiveProperty used an untyped default parameter
 *     → `defaultValue: unknown` with `unknown` return type
 *   • getResponsiveProperty used an unchecked breakpoint style cast
 *     → getBreakpointStyleProperty() from utils (returns unknown)
 *   • outerWrapperStyle used an unchecked style record cast
 *     → `style as Record<string, unknown>` + `return rest as React.CSSProperties`
 *     (sound: we only remove keys from a React.CSSProperties object)
 *
 * Group D — missing property on typed interface
 *   • SharedDataTextComponent accessed refreshBadgeScopeId through an unchecked props cast
 *     → `props.refreshBadgeScopeId` (refreshBadgeScopeId is in DataTextProperties)
 */
import React, {
  useMemo,
  useCallback,
  useState,
  useEffect,
  useRef,
} from "react";
import { Component, ComponentType, Viewport, DataTextProperties } from "@/types";
import type { TextSegment } from "@/types/ai";
import { isRichTextContent, segmentsToPlainText } from "@/types/ai";
import { cn } from "@/lib/utils";
import { AlertCircle, RefreshCw } from "lucide-react";
import { useEditorStore } from "@/stores/editor/editor-store";
import { useAuthStore } from "@/stores/auth/auth-store";
import { useDebugStore } from "@/stores/ai/debug-store";
import { useTextSelection } from "@/hooks/editor/useTextSelection";
import { useTextEditing } from "@/hooks/editor/useTextEditing";
import { ExpressionEvaluator } from "@/services/expression/ExpressionEvaluator";
import { fullyDecodeHtmlEntities } from "@/utils/htmlEntities";
import { ChartDataSource } from "@/types/chartDataSource";
import { formatMetricValue } from "@/utils/PropertyFormatters";
import {
  extractTimeZoneFromRecord,
  findRawTemporalField,
  getBrowserTimeZone,
  isTemporalFieldName,
  resolvePreferredTimeZone,
} from "@/utils/dateTimeFormatting";
import {
  useQueryTemplateData,
  isQueryTemplateDataSource,
} from "@/hooks/useQueryTemplateData";
import {
  useChartHoverData,
  useChartData,
} from "@/stores/chart/chart-hover-store";
import { useRefreshBadge } from "@/hooks/useRefreshBadge";
import {
  useComponentDataStore,
  useMultiComponentData,
  useAllDependenciesLoaded,
} from "@/stores/editor/component-data-store";
import { DataTextLoadingSkeleton } from "@/components/common/DataLoadingSkeletons";
import {
  isQueryTemplateDependencyId,
  useComputedExpressionDependencies,
} from "@/hooks/useComputedExpressionDependencies";
import { getBreakpointStyleProperty } from "./utils";

export interface TextRendererProps {
  component: Component;
  viewport?: Viewport;
  allComponents?: Component[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers shared between the two renderers
// ─────────────────────────────────────────────────────────────────────────────

function lookupValueWithAliasFallback(
  row: Record<string, unknown>,
  field: string,
  measures?: Array<{ field: string; aggregation: string; alias?: string }>,
): unknown {
  const candidates = new Set<string>([field]);

  if (field !== "value") candidates.add("value");

  const aggregationPrefixes = [
    "last_",
    "first_",
    "avg_",
    "sum_",
    "count_",
    "min_",
    "max_",
    "none_",
  ];
  for (const prefix of aggregationPrefixes) {
    if (field.startsWith(prefix)) {
      candidates.add(field.slice(prefix.length));
    }
  }

  for (const measure of measures || []) {
    if (measure.alias) candidates.add(measure.alias);
    candidates.add(
      `${measure.aggregation}_${measure.field.replace(/\./g, "_")}`,
    );
    candidates.add(measure.field);
    candidates.add(measure.field.replace(/^data\./, ""));
  }

  for (const candidate of candidates) {
    const value = row[candidate];
    if (value !== undefined && value !== null) return value;
  }

  return undefined;
}

function enrichRowsWithAliasFallbacks(
  rows: Record<string, unknown>[] | undefined,
  valueField?: string,
  measures?: Array<{ field: string; aggregation: string; alias?: string }>,
): Record<string, unknown>[] | undefined {
  if (!rows?.length) return rows;

  return rows.map((row) => {
    let enriched: Record<string, unknown> | null = null;

    const ensureAlias = (alias: string | undefined, value: unknown) => {
      if (
        !alias ||
        value === undefined ||
        value === null ||
        row[alias] !== undefined
      )
        return;
      if (!enriched) enriched = { ...row };
      enriched[alias] = value;
    };

    if (valueField) {
      ensureAlias(
        valueField,
        lookupValueWithAliasFallback(row, valueField, measures),
      );
    }

    for (const measure of measures || []) {
      const defaultAlias = `${measure.aggregation}_${measure.field.replace(/\./g, "_")}`;
      const rawField = measure.field.replace(/^data\./, "");
      const value = lookupValueWithAliasFallback(
        row,
        measure.alias || defaultAlias,
        measures,
      );
      ensureAlias(measure.alias, value);
      ensureAlias(defaultAlias, value);
      ensureAlias(measure.field, value);
      ensureAlias(rawField, value);
    }

    return enriched || row;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// TextRenderer (was SharedTextComponent)
// ─────────────────────────────────────────────────────────────────────────────

const TextRenderer: React.FC<{
  component: Component;
  allComponents?: Component[];
  viewport?: Viewport;
}> = ({ component, allComponents, viewport }) => {
  const { properties = {}, style, responsivePosition } = component;
  const updateComponent = useEditorStore((state) => state.updateComponent);

  // Phase 5: Text selection support - only enable in editor mode (not viewer)
  const isEditorMode =
    typeof window !== "undefined" &&
    !window.location.pathname.includes("/viewer/") &&
    !window.location.pathname.includes("/preview/");

  // Get responsive properties based on viewport.
  // Replaces the previous untyped default and unchecked breakpoint-style lookup.
  const getResponsiveProperty = (
    propName: string,
    defaultValue: unknown,
  ): unknown => {
    if (!viewport || !responsivePosition?.breakpoints) return defaultValue;

    const breakpoint =
      viewport === Viewport.MOBILE
        ? "mobile"
        : viewport === Viewport.TABLET
          ? "tablet"
          : "desktop";

    const breakpointStyle = responsivePosition.breakpoints[breakpoint]?.style;
    // getBreakpointStyleProperty returns unknown and keeps the lookup narrowed.
    return (
      getBreakpointStyleProperty(
        breakpointStyle as Record<string, unknown> | undefined,
        propName,
      ) ?? defaultValue
    );
  };

  // Phase 5: Handle content - support both plain text and rich text (TextSegment[])
  const rawContent = properties.content;

  let textContent = "";
  let richTextSegments: TextSegment[] | null = null;

  if (
    rawContent !== undefined &&
    rawContent !== null &&
    isRichTextContent(rawContent)
  ) {
    richTextSegments = (rawContent as TextSegment[]).map((seg) => ({
      ...seg,
      text: fullyDecodeHtmlEntities(seg.text),
    }));
    textContent = segmentsToPlainText(richTextSegments);
    console.log("[Phase 5.4] Rich text detected:", {
      segmentCount: richTextSegments.length,
      segments: richTextSegments,
    });
  } else {
    textContent =
      rawContent !== undefined &&
      rawContent !== null &&
      typeof rawContent === "string"
        ? fullyDecodeHtmlEntities(rawContent)
        : rawContent !== undefined &&
            rawContent !== null &&
            typeof rawContent === "number"
          ? String(rawContent)
          : "Text";

    if (allComponents && ExpressionEvaluator.hasExpressions(textContent)) {
      textContent = ExpressionEvaluator.evaluate(textContent, {
        components: allComponents,
        currentComponent: component,
      });
    }
  }

  const { textRef, storedSelection, hasSelection, clearSelection } = useTextSelection({
    componentId: component.id,
    fullText: textContent,
    enabled: isEditorMode,
  });
  const suppressNextWrapperClickRef = useRef(false);

  const {
    isEditing,
    editableRef,
    handleKeyDown,
    handleInput,
    handleBlur,
    handleDoubleClick,
  } = useTextEditing({
    componentId: component.id,
    initialContent: richTextSegments || textContent,
    enabled: isEditorMode,
    onSave: (newContent) => {
      console.log("[SharedTextComponent] Saving edited content:", newContent);
      updateComponent(component.id, {
        properties: { content: newContent },
      });
    },
    onCancel: () => {
      console.log("[SharedTextComponent] Edit cancelled");
    },
  });

  useEffect(() => {
    if (isEditing) {
      console.log('[TextRenderer] clearing stored selection because inline editing started', {
        componentId: component.id,
      });
      clearSelection();
    }
  }, [isEditing, clearSelection, component.id]);

  const stopEditingMousePropagation = useCallback((e: React.MouseEvent) => {
    if (isEditing) {
      e.stopPropagation();
    }
  }, [isEditing]);

  const handleSelectableMouseUp = useCallback((e: React.MouseEvent) => {
    if (isEditing || !textRef.current) {
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      suppressNextWrapperClickRef.current = false;
      console.log('[TextRenderer] mouseup: no browser selection to preserve', {
        componentId: component.id,
      });
      return;
    }

    const range = selection.getRangeAt(0);
    const hasRangeSelection =
      !selection.isCollapsed &&
      textRef.current.contains(range.commonAncestorContainer);

    suppressNextWrapperClickRef.current = hasRangeSelection;

    console.log('[TextRenderer] mouseup selection state', {
      componentId: component.id,
      selectedText: selection.toString(),
      collapsed: selection.isCollapsed,
      suppressNextWrapperClick: hasRangeSelection,
    });

    if (hasRangeSelection) {
      e.stopPropagation();
    }
  }, [isEditing, textRef, component.id]);

  const stopSelectionMousePropagation = useCallback((e: React.MouseEvent) => {
    if (isEditing || !textRef.current) {
      return;
    }

    if (suppressNextWrapperClickRef.current) {
      console.log('[TextRenderer] suppressing wrapper click after text selection gesture', {
        componentId: component.id,
      });
      suppressNextWrapperClickRef.current = false;
      e.stopPropagation();
      return;
    }

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
      console.log('[TextRenderer] selection propagation not stopped', {
          componentId: component.id,
        reason: !selection || selection.rangeCount === 0
          ? 'no-selection'
          : 'collapsed-selection',
      });
      return;
    }

    const range = selection.getRangeAt(0);
    if (textRef.current.contains(range.commonAncestorContainer)) {
      console.log('[TextRenderer] stopping click propagation for active text selection', {
        componentId: component.id,
        selectedText: selection.toString(),
      });
      e.stopPropagation();
    }
  }, [isEditing, textRef, component.id]);

  const handleTextDoubleClick = useCallback((e: React.MouseEvent) => {
    if (isEditing) {
      e.stopPropagation();
      return;
    }

    if (isEditorMode) {
      handleDoubleClick(e);
    }
  }, [isEditing, isEditorMode, handleDoubleClick]);

  const renderTextWithSelection = (text: string) => {
    if (!hasSelection || !storedSelection) {
      return text;
    }

    const { startIndex, endIndex } = storedSelection;
    const beforeSelection = text.substring(0, startIndex);
    const selectedText = text.substring(startIndex, endIndex);
    const afterSelection = text.substring(endIndex);

    return (
      <>
        {beforeSelection}
        <span
          style={{
            backgroundColor: "rgba(59, 130, 246, 0.3)",
            borderRadius: "2px",
            padding: "0 1px",
          }}
          data-text-selection="true"
        >
          {selectedText}
        </span>
        {afterSelection}
      </>
    );
  };

  const renderRichTextSegments = (segments: TextSegment[]) => {
    console.log("[Phase 5.4] Rendering rich text segments:", segments);

    const selectionHighlightStyle: React.CSSProperties = {
      backgroundColor: "rgba(59, 130, 246, 0.3)",
      borderRadius: "2px",
    };

    let charOffset = 0;

    return segments.map((segment, index) => {
      const segmentStart = charOffset;
      const segmentEnd = charOffset + segment.text.length;
      charOffset = segmentEnd;

      const segmentStyle: React.CSSProperties = {};

      if (segment.style) {
        if (segment.style.fontWeight)
          segmentStyle.fontWeight = segment.style.fontWeight;
        if (segment.style.fontStyle)
          segmentStyle.fontStyle =
            segment.style.fontStyle as React.CSSProperties["fontStyle"];
        if (segment.style.textDecoration)
          segmentStyle.textDecoration =
            segment.style.textDecoration as React.CSSProperties["textDecoration"];
        if (segment.style.textDecorationLine)
          segmentStyle.textDecorationLine =
            segment.style
              .textDecorationLine as React.CSSProperties["textDecorationLine"];
        if (segment.style.textDecorationStyle)
          segmentStyle.textDecorationStyle =
            segment.style
              .textDecorationStyle as React.CSSProperties["textDecorationStyle"];
        if (segment.style.textDecorationColor)
          segmentStyle.textDecorationColor = segment.style.textDecorationColor;
        if (segment.style.color) segmentStyle.color = segment.style.color;
        if (segment.style.backgroundColor)
          segmentStyle.backgroundColor = segment.style.backgroundColor;
        if (segment.style.fontSize)
          segmentStyle.fontSize = segment.style.fontSize;
        if (segment.style.fontFamily)
          segmentStyle.fontFamily = segment.style.fontFamily;
        if (segment.style.letterSpacing)
          segmentStyle.letterSpacing = segment.style.letterSpacing;
        if (segment.style.textTransform)
          segmentStyle.textTransform =
            segment.style.textTransform as React.CSSProperties["textTransform"];
        if (segment.style.textShadow)
          segmentStyle.textShadow = segment.style.textShadow;
        if (segment.style.opacity)
          segmentStyle.opacity = segment.style.opacity;
      }

      const hasCustomStyles = Object.keys(segmentStyle).length > 0;

      if (hasSelection && storedSelection) {
        const selStart = storedSelection.startIndex;
        const selEnd = storedSelection.endIndex;

        if (segmentEnd > selStart && segmentStart < selEnd) {
          const overlapStart = Math.max(0, selStart - segmentStart);
          const overlapEnd = Math.min(
            segment.text.length,
            selEnd - segmentStart,
          );

          const beforeText = segment.text.substring(0, overlapStart);
          const selectedText = segment.text.substring(overlapStart, overlapEnd);
          const afterText = segment.text.substring(overlapEnd);

          return (
            <span
              key={`segment-${index}`}
              style={hasCustomStyles ? segmentStyle : undefined}
              data-segment-index={index}
            >
              {beforeText}
              {selectedText && (
                <span
                  style={{ ...selectionHighlightStyle }}
                  data-text-selection="true"
                >
                  {selectedText}
                </span>
              )}
              {afterText}
            </span>
          );
        }
      }

      return (
        <span
          key={`segment-${index}`}
          style={hasCustomStyles ? segmentStyle : undefined}
          data-segment-index={index}
        >
          {segment.text}
        </span>
      );
    });
  };

  // Exclude gradient-related styles from outer wrapper.
  // Replaces the previous unchecked style record cast with `Record<string, unknown>`.
  // The `return rest as React.CSSProperties` is a sound cast: we only remove
  // known gradient/decoration keys from a React.CSSProperties object.
  const outerWrapperStyle = useMemo((): React.CSSProperties => {
    if (!style) return {};
    let {
      background,
      WebkitBackgroundClip,
      WebkitTextFillColor,
      backgroundClip,
      textShadow,
      WebkitTextStroke,
      textDecorationLine,
      textDecorationStyle,
      textDecorationColor,
      ...rest
    } = style as Record<string, unknown>;
    void background;
    void WebkitBackgroundClip;
    void WebkitTextFillColor;
    void backgroundClip;
    void textShadow;
    void WebkitTextStroke;
    void textDecorationLine;
    void textDecorationStyle;
    void textDecorationColor;

    const shorthands = [
      {
        key: "margin",
        sides: ["marginTop", "marginRight", "marginBottom", "marginLeft"],
      },
      {
        key: "padding",
        sides: ["paddingTop", "paddingRight", "paddingBottom", "paddingLeft"],
      },
    ];
    for (const { key, sides } of shorthands) {
      if (rest[key] === undefined) continue;
      if (!sides.some((s) => rest[s] !== undefined)) continue;
      const parts = String(rest[key]).trim().split(/\s+/);
      const expanded =
        parts.length === 1
          ? [parts[0], parts[0], parts[0], parts[0]]
          : parts.length === 2
            ? [parts[0], parts[1], parts[0], parts[1]]
            : parts.length === 3
              ? [parts[0], parts[1], parts[2], parts[1]]
              : [parts[0], parts[1], parts[2], parts[3]];
      for (let i = 0; i < sides.length; i++) {
        if (rest[sides[i]] === undefined) rest[sides[i]] = expanded[i];
      }
      const { [key]: _removed, ...nextRest } = rest;
      void _removed;
      rest = nextRest;
    }
    // Sound cast: rest contains only valid CSSProperties keys since we started
    // from React.CSSProperties and only removed specific known gradient keys.
    return rest as React.CSSProperties;
  }, [style]);

  return (
    <div
      className={cn(
        "flex items-center justify-center w-full min-w-0",
        properties.overflow === "hidden" && "overflow-hidden",
      )}
      style={{
        ...outerWrapperStyle,
        backgroundColor:
          typeof style?.backgroundColor === "string" &&
          style.backgroundColor.trim() !== ""
            ? style.backgroundColor
            : typeof properties.backgroundColor === "string" &&
                (properties.backgroundColor as string).trim() !== ""
              ? (properties.backgroundColor as string)
              : undefined,
        border:
          typeof style?.border === "string" && style.border.trim() !== ""
            ? style.border
            : typeof properties.border === "string" &&
                (properties.border as string).trim() !== ""
              ? (properties.border as string)
              : undefined,
        borderRadius:
          typeof style?.borderRadius === "string" &&
          style.borderRadius.trim() !== ""
            ? style.borderRadius
            : typeof properties.borderRadius === "string" &&
                (properties.borderRadius as string).trim() !== ""
              ? (properties.borderRadius as string)
              : undefined,
        ...(() => {
          const responsivePadding = getResponsiveProperty(
            "padding",
            properties.padding,
          );
          const paddingVal =
            (typeof style?.padding === "string" &&
              style.padding.trim() !== "") ||
            (typeof style?.padding === "number" && !isNaN(style.padding))
              ? style.padding
              : (typeof responsivePadding === "string" &&
                    responsivePadding.trim() !== "") ||
                  (typeof responsivePadding === "number" &&
                    !isNaN(responsivePadding))
                ? responsivePadding
                : undefined;
          if (paddingVal !== undefined) {
            const parts = String(paddingVal).trim().split(/\s+/);
            const exp =
              parts.length === 1
                ? [parts[0], parts[0], parts[0], parts[0]]
                : parts.length === 2
                  ? [parts[0], parts[1], parts[0], parts[1]]
                  : parts.length === 3
                    ? [parts[0], parts[1], parts[2], parts[1]]
                    : [parts[0], parts[1], parts[2], parts[3]];
            return {
              paddingTop: exp[0],
              paddingRight: exp[1],
              paddingBottom: exp[2],
              paddingLeft: exp[3],
            };
          }
          return {};
        })(),
        paddingTop:
          (typeof properties.paddingTop === "string" &&
            (properties.paddingTop as string).trim() !== "") ||
          (typeof properties.paddingTop === "number" &&
            !isNaN(properties.paddingTop as number))
            ? (properties.paddingTop as string | number)
            : undefined,
        paddingBottom:
          (typeof properties.paddingBottom === "string" &&
            (properties.paddingBottom as string).trim() !== "") ||
          (typeof properties.paddingBottom === "number" &&
            !isNaN(properties.paddingBottom as number))
            ? (properties.paddingBottom as string | number)
            : undefined,
        paddingLeft:
          (typeof properties.paddingLeft === "string" &&
            (properties.paddingLeft as string).trim() !== "") ||
          (typeof properties.paddingLeft === "number" &&
            !isNaN(properties.paddingLeft as number))
            ? (properties.paddingLeft as string | number)
            : undefined,
        paddingRight:
          (typeof properties.paddingRight === "string" &&
            (properties.paddingRight as string).trim() !== "") ||
          (typeof properties.paddingRight === "number" &&
            !isNaN(properties.paddingRight as number))
            ? (properties.paddingRight as string | number)
            : undefined,
        ...(() => {
          const marginVal =
            (typeof style?.margin === "string" &&
              style.margin.trim() !== "") ||
            (typeof style?.margin === "number" && !isNaN(style.margin))
              ? style.margin
              : (typeof properties.margin === "string" &&
                    (properties.margin as string).trim() !== "") ||
                  (typeof properties.margin === "number" &&
                    !isNaN(properties.margin as number))
                ? (properties.margin as string | number)
                : undefined;
          if (marginVal !== undefined) {
            const parts = String(marginVal).trim().split(/\s+/);
            const exp =
              parts.length === 1
                ? [parts[0], parts[0], parts[0], parts[0]]
                : parts.length === 2
                  ? [parts[0], parts[1], parts[0], parts[1]]
                  : parts.length === 3
                    ? [parts[0], parts[1], parts[2], parts[1]]
                    : [parts[0], parts[1], parts[2], parts[3]];
            return {
              marginTop: exp[0],
              marginRight: exp[1],
              marginBottom: exp[2],
              marginLeft: exp[3],
            };
          }
          return {};
        })(),
        position: "relative",
        zIndex: 1,
        transition:
          typeof properties.transition === "string"
            ? (properties.transition as string)
            : undefined,
      }}
    >
      <div
        key={
          isEditing ? `editing-${component.id}` : `viewing-${component.id}`
        }
        ref={isEditing ? editableRef : textRef}
        contentEditable={isEditing}
        suppressContentEditableWarning={isEditing}
        spellCheck={false}
        data-editing={isEditing ? "true" : "false"}
        onKeyDown={isEditing ? handleKeyDown : undefined}
        onInput={isEditing ? handleInput : undefined}
        onBlur={isEditing ? handleBlur : undefined}
        onMouseDown={isEditing ? stopEditingMousePropagation : undefined}
        onMouseUp={!isEditing ? handleSelectableMouseUp : undefined}
        onClick={isEditing ? stopEditingMousePropagation : stopSelectionMousePropagation}
        onDoubleClick={isEditorMode ? handleTextDoubleClick : undefined}
        style={(() => {
          const responsiveTextAlign = getResponsiveProperty(
            "textAlign",
            properties.textAlign,
          );
          const effectiveTextAlign =
            typeof responsiveTextAlign === "string" &&
            [
              "left",
              "center",
              "right",
              "justify",
              "start",
              "end",
            ].includes(responsiveTextAlign)
              ? responsiveTextAlign
              : "left";

          const isRichTextMode =
            richTextSegments && richTextSegments.length > 0;
          const isTruncationMode =
            properties.overflow === "hidden" &&
            properties.textOverflow === "ellipsis";

          return {
            display:
              isRichTextMode || isTruncationMode ? "block" : "flex",
            alignItems:
              isRichTextMode || isTruncationMode
                ? undefined
                : effectiveTextAlign === "center"
                  ? "center"
                  : "flex-start",
            justifyContent:
              isRichTextMode || isTruncationMode
                ? undefined
                : effectiveTextAlign === "center"
                  ? "center"
                  : effectiveTextAlign === "right"
                    ? "flex-end"
                    : "flex-start",
            ...(isEditing && {
              outline: "2px solid #3b82f6",
              outlineOffset: "2px",
              background: "rgba(59, 130, 246, 0.05)",
              cursor: "text",
            }),
            fontSize: (() => {
              const responsiveFontSize = getResponsiveProperty(
                "fontSize",
                properties.fontSize,
              );
              if (
                typeof responsiveFontSize === "number" ||
                typeof responsiveFontSize === "string"
              ) {
                return responsiveFontSize;
              }
              if (
                typeof style?.fontSize === "string" ||
                typeof style?.fontSize === "number"
              ) {
                return style.fontSize;
              }
              return 16;
            })(),
            fontWeight: (() => {
              const responsiveFontWeight = getResponsiveProperty(
                "fontWeight",
                properties.fontWeight,
              );
              return typeof responsiveFontWeight === "number" ||
                typeof responsiveFontWeight === "string"
                ? responsiveFontWeight
                : "normal";
            })(),
            textAlign: effectiveTextAlign as React.CSSProperties["textAlign"],
            color: (() => {
              const responsiveColor = getResponsiveProperty(
                "color",
                properties.color,
              );
              return typeof responsiveColor === "string" &&
                responsiveColor.trim() !== ""
                ? responsiveColor
                : typeof style?.color === "string" &&
                    style.color.trim() !== ""
                  ? style.color
                  : undefined;
            })(),
            fontFamily:
              typeof properties.fontFamily === "string" &&
              (properties.fontFamily as string).trim() !== ""
                ? (properties.fontFamily as string)
                : undefined,
            fontStyle: (() => {
              const responsiveFontStyle = getResponsiveProperty(
                "fontStyle",
                properties.fontStyle,
              );
              return typeof responsiveFontStyle === "string" &&
                responsiveFontStyle.trim() !== ""
                ? (responsiveFontStyle as React.CSSProperties["fontStyle"])
                : typeof style?.fontStyle === "string" &&
                    (style.fontStyle as string).trim() !== ""
                  ? (style.fontStyle as React.CSSProperties["fontStyle"])
                  : undefined;
            })(),
            lineHeight: (() => {
              const responsiveLineHeight = getResponsiveProperty(
                "lineHeight",
                properties.lineHeight,
              );
              return typeof responsiveLineHeight === "string" ||
                typeof responsiveLineHeight === "number"
                ? responsiveLineHeight
                : 1.5;
            })(),
            letterSpacing: (() => {
              const responsiveLetterSpacing = getResponsiveProperty(
                "letterSpacing",
                properties.letterSpacing,
              );
              return typeof responsiveLetterSpacing === "string" ||
                typeof responsiveLetterSpacing === "number"
                ? responsiveLetterSpacing
                : undefined;
            })(),
            textDecoration: (() => {
              const responsiveTextDecoration = getResponsiveProperty(
                "textDecoration",
                properties.textDecoration,
              );
              return typeof responsiveTextDecoration === "string"
                ? (responsiveTextDecoration as React.CSSProperties["textDecoration"])
                : undefined;
            })(),
            textTransform: (() => {
              const responsiveTextTransform = getResponsiveProperty(
                "textTransform",
                properties.textTransform,
              );
              return typeof responsiveTextTransform === "string"
                ? (responsiveTextTransform as React.CSSProperties["textTransform"])
                : undefined;
            })(),
            wordBreak:
              typeof properties.wordBreak === "string"
                ? (properties.wordBreak as React.CSSProperties["wordBreak"])
                : "break-word",
            whiteSpace:
              typeof properties.whiteSpace === "string"
                ? (properties.whiteSpace as React.CSSProperties["whiteSpace"])
                : "pre-wrap",
            textOverflow:
              typeof properties.textOverflow === "string"
                ? (properties.textOverflow as React.CSSProperties["textOverflow"])
                : undefined,
            width:
              typeof properties.width === "string" ||
              typeof properties.width === "number"
                ? (properties.width as string | number)
                : "100%",
            height:
              typeof properties.height === "string" ||
              typeof properties.height === "number"
                ? (properties.height as string | number)
                : "auto",
            overflow:
              typeof properties.overflow === "string"
                ? (properties.overflow as React.CSSProperties["overflow"])
                : "visible",
            minWidth: isTruncationMode ? 0 : undefined,
            background:
              typeof style?.background === "string"
                ? style.background
                : undefined,
            WebkitBackgroundClip:
              typeof style?.WebkitBackgroundClip === "string"
                ? style.WebkitBackgroundClip
                : undefined,
            WebkitTextFillColor:
              typeof style?.WebkitTextFillColor === "string"
                ? style.WebkitTextFillColor
                : undefined,
            backgroundClip:
              typeof style?.backgroundClip === "string"
                ? style.backgroundClip
                : undefined,
            textShadow:
              typeof style?.textShadow === "string"
                ? style.textShadow
                : undefined,
            WebkitTextStroke:
              typeof style?.WebkitTextStroke === "string"
                ? style.WebkitTextStroke
                : undefined,
            ...(!getResponsiveProperty(
              "textDecoration",
              properties.textDecoration,
            ) && {
              textDecorationLine:
                typeof style?.textDecorationLine === "string"
                  ? (style.textDecorationLine as React.CSSProperties["textDecorationLine"])
                  : undefined,
              textDecorationStyle:
                typeof style?.textDecorationStyle === "string"
                  ? (style.textDecorationStyle as React.CSSProperties["textDecorationStyle"])
                  : undefined,
              textDecorationColor:
                typeof style?.textDecorationColor === "string"
                  ? style.textDecorationColor
                  : undefined,
            }),
          };
        })()}
      >
        {isEditing
          ? null
          : richTextSegments
            ? renderRichTextSegments(richTextSegments)
            : renderTextWithSelection(textContent)}
      </div>
    </div>
  );
};

TextRenderer.displayName = "TextRenderer";

// ─────────────────────────────────────────────────────────────────────────────
// DataTextRenderer (was SharedDataTextComponent)
// ─────────────────────────────────────────────────────────────────────────────

/** Chart component types used for linked-chart auto-discovery. Module-level to
 *  avoid inclusion in useMemo dependency arrays. */
const CHART_TYPES = ["line-chart", "area-chart", "bar-chart"];

const DataTextRenderer: React.FC<{
  component: Component;
  viewport?: Viewport;
  allComponents?: Component[];
}> = ({ component, viewport, allComponents }) => {
  const { properties = {}, style = {}, responsivePosition } = component;
  const props = properties as DataTextProperties;

  // DATA FETCHING
  const dataSource = props.dataSource as ChartDataSource | undefined;
  const templateId = props.queryTemplateId as string | undefined;
  const hasQueryTemplate = isQueryTemplateDataSource(dataSource);
  const hasWorkflow =
    dataSource?.type === "workflow" &&
    !!dataSource?.workflowConfig?.workflowId;
  const isTimestampAggregate = dataSource?.type === "timestamp-aggregate";
  // Replaces the previous unchecked refreshBadgeScopeId props access. refreshBadgeScopeId
  // is declared in DataTextProperties (types/index.ts:1137).
  const scopeId = props.refreshBadgeScopeId;
  const lastRuntimeDiagnosticKeyRef = useRef<string | null>(null);
  const authUser = useAuthStore((state) => state.user);

  const badgeData = useRefreshBadge({
    scopeContainerId: isTimestampAggregate ? scopeId : undefined,
    enabled: isTimestampAggregate,
  });

  const {
    data: queryData,
    isLoading,
    error,
    refetch,
  } = useQueryTemplateData({
    dataSource,
    componentId: component.id,
    enabled:
      (hasQueryTemplate || hasWorkflow || !!templateId) &&
      !isTimestampAggregate,
    templateId,
  });
  const queryMeasures = dataSource?.queryDefinition?.measures as
    | Array<{ field: string; aggregation: string; alias?: string }>
    | undefined;
  const normalizedQueryData = useMemo(
    () =>
      enrichRowsWithAliasFallbacks(
        queryData ?? undefined,
        props.valueField,
        queryMeasures,
      ),
    [queryData, props.valueField, queryMeasures],
  );

  // CHART HOVER SYNCHRONIZATION
  const linkedChartId = props.linkedChartId;
  const resolvedLinkedChartId = useMemo(() => {
    if (!linkedChartId || !allComponents?.length) return linkedChartId;

    if (allComponents.some((c) => c.id === linkedChartId))
      return linkedChartId;

    const byNodeId = allComponents.find(
      (c) => c.properties?._templateNodeId === linkedChartId,
    );
    if (byNodeId) return byNodeId.id;

    let current: Component | undefined = component;
    for (let i = 0; i < 4; i++) {
      const currentParentId: string | undefined = current?.parentId;
      if (!currentParentId) break;
      const chart = allComponents.find(
        (c) =>
          c.parentId === currentParentId &&
          CHART_TYPES.includes(c.type as string) &&
          c.id !== component.id,
      );
      if (chart) return chart.id;
      current = allComponents.find((c) => c.id === currentParentId);
    }

    return linkedChartId;
  }, [linkedChartId, allComponents, component]);

  const hoverData = useChartHoverData(resolvedLinkedChartId);
  const showCurrentOnIdle = props.showCurrentOnIdle !== false;

  const chartStoreData = useChartData(resolvedLinkedChartId);

  const linkedChartData = useMemo(() => {
    if (!resolvedLinkedChartId || chartStoreData || !allComponents?.length)
      return null;
    const chart = allComponents.find(
      (c) => c.id === resolvedLinkedChartId,
    );
    const data = chart?.properties?.data;
    return Array.isArray(data)
      ? (data as Record<string, unknown>[])
      : null;
  }, [resolvedLinkedChartId, chartStoreData, allComponents]);

  const chartData = chartStoreData || linkedChartData;

  const extractChartValue = useCallback(
    (
      dataPoint: Record<string, unknown>,
      fieldToExtract: string,
    ): string | number | null => {
      const rawTemporalField = isTemporalFieldName(fieldToExtract)
        ? findRawTemporalField(dataPoint, fieldToExtract)
        : undefined;
      const preferredField = rawTemporalField || fieldToExtract;
      const val = dataPoint[preferredField];
      if (
        val !== undefined &&
        val !== null &&
        (typeof val === "string" || typeof val === "number")
      ) {
        return val;
      }

      const keys = Object.keys(dataPoint);
      const fuzzyKey = keys.find(
        (k) =>
          k.includes(fieldToExtract) || fieldToExtract.includes(k),
      );
      if (fuzzyKey) {
        const fuzzyVal = dataPoint[fuzzyKey];
        if (
          fuzzyVal !== undefined &&
          fuzzyVal !== null &&
          (typeof fuzzyVal === "string" || typeof fuzzyVal === "number")
        ) {
          return fuzzyVal;
        }
      }

      for (const [k, v] of Object.entries(dataPoint)) {
        if (k === "_id" || k === "id" || k.startsWith("_")) continue;
        if (typeof v === "number") return v;
      }

      return null;
    },
    [],
  );

  const linkedChartCurrentValue = useMemo(() => {
    if (!showCurrentOnIdle) return null;
    if (
      !chartData ||
      !Array.isArray(chartData) ||
      chartData.length === 0
    )
      return null;

    const lastDataPoint = chartData[
      chartData.length - 1
    ] as Record<string, unknown>;
    const fieldToExtract =
      props.hoverValueField || props.valueField || "value";
    return extractChartValue(lastDataPoint, fieldToExtract);
  }, [
    chartData,
    showCurrentOnIdle,
    props.hoverValueField,
    props.valueField,
    extractChartValue,
  ]);

  const isHovering = !!(resolvedLinkedChartId && hoverData?.dataPoint);

  const hoverValue = useMemo(() => {
    if (!isHovering || !hoverData?.dataPoint) return null;

    const fieldToExtract =
      props.hoverValueField || props.valueField || "value";
    return extractChartValue(
      hoverData.dataPoint as Record<string, unknown>,
      fieldToExtract,
    );
  }, [
    isHovering,
    hoverData?.dataPoint,
    props.hoverValueField,
    props.valueField,
    extractChartValue,
  ]);

  // COMPUTED EXPRESSION SUPPORT
  const computedExpression = props.computedExpression;

  const expressionDependencyIds = useMemo(() => {
    if (!computedExpression) return [];
    if (props.expressionDependencies?.length)
      return props.expressionDependencies;
    return ExpressionEvaluator.extractComponentReferences(computedExpression);
  }, [computedExpression, props.expressionDependencies]);

  const componentDependencyIds = useMemo(
    () =>
      expressionDependencyIds.filter(
        (id) => !isQueryTemplateDependencyId(id),
      ),
    [expressionDependencyIds],
  );
  const queryTemplateDependencyIds = useMemo(
    () => expressionDependencyIds.filter(isQueryTemplateDependencyId),
    [expressionDependencyIds],
  );
  const componentDependencyData =
    useMultiComponentData(componentDependencyIds);
  const componentDepsLoadedRaw =
    useAllDependenciesLoaded(componentDependencyIds);
  const componentDepsLoaded =
    componentDependencyIds.length === 0 || componentDepsLoadedRaw;
  const {
    dependencyData: queryTemplateDependencyData,
    dependencyErrors: queryTemplateDependencyErrors,
    allLoaded: queryTemplateDepsLoaded,
  } = useComputedExpressionDependencies(queryTemplateDependencyIds);
  const dependencyData = useMemo(
    () => ({
      ...componentDependencyData,
      ...queryTemplateDependencyData,
    }),
    [componentDependencyData, queryTemplateDependencyData],
  );
  const allDepsLoaded = componentDepsLoaded && queryTemplateDepsLoaded;
  const computedReferenceDiagnostics = useMemo(() => {
    if (!computedExpression) return [];
    return ExpressionEvaluator.inspectMathReferences(computedExpression, {
      currentComponentId: component.id,
      currentQueryData: normalizedQueryData,
      dependencyData,
      allComponents,
    });
  }, [
    allComponents,
    component.id,
    computedExpression,
    dependencyData,
    normalizedQueryData,
  ]);

  const dataTextMode = useMemo(() => {
    if (isTimestampAggregate) return "timestamp-aggregate" as const;
    if (computedExpression) return "computed" as const;
    if (templateId || hasQueryTemplate) return "query-template" as const;
    if (hasWorkflow) return "workflow" as const;
    return "static" as const;
  }, [
    computedExpression,
    hasQueryTemplate,
    hasWorkflow,
    isTimestampAggregate,
    templateId,
  ]);

  // VALUE EXTRACTION
  const extractedValue: string | number | null = useMemo(() => {
    if (isTimestampAggregate) {
      return badgeData.formattedTime;
    }

    if (resolvedLinkedChartId && isHovering && hoverValue !== null) {
      return hoverValue;
    }

    if (resolvedLinkedChartId && linkedChartCurrentValue !== null) {
      return linkedChartCurrentValue;
    }

    if (computedExpression && allDepsLoaded) {
      const result = ExpressionEvaluator.evaluateMathExpression(
        computedExpression,
        {
          currentComponentId: component.id,
          currentQueryData: normalizedQueryData,
          dependencyData,
          allComponents,
        },
      );
      if (result !== null) {
        return result;
      }
    }

    if (
      (templateId || hasQueryTemplate || hasWorkflow) &&
      normalizedQueryData &&
      normalizedQueryData.length > 0
    ) {
      const row = normalizedQueryData[0];
      const field = props.valueField || "value";
      const rawTemporalField = isTemporalFieldName(field)
        ? findRawTemporalField(row, field)
        : undefined;
      let val = lookupValueWithAliasFallback(
        row,
        rawTemporalField || field,
        queryMeasures,
      );
      if ((val === undefined || val === null) && queryMeasures?.length) {
        const preferred = queryMeasures.filter(
          (m) => m.aggregation !== "count",
        );
        const fallback = queryMeasures.filter(
          (m) => m.aggregation === "count",
        );
        for (const measure of [...preferred, ...fallback]) {
          val = lookupValueWithAliasFallback(
            row,
            measure.alias ||
              `${measure.aggregation}_${measure.field.replace(/\./g, "_")}`,
            [measure],
          );
          if (val !== undefined && val !== null) break;
        }
      }
      if (val === undefined || val === null) {
        for (const [key, v] of Object.entries(row)) {
          if (key === "_id" || key === "id") continue;
          if (typeof v === "number") {
            val = v;
            break;
          }
          if (
            typeof v === "string" &&
            v.trim() !== "" &&
            !Number.isNaN(Number(v))
          ) {
            val = v;
            break;
          }
        }
      }
      if (
        val !== undefined &&
        val !== null &&
        (typeof val === "string" || typeof val === "number")
      ) {
        return val;
      }
    }

    const value = props.value;
    if (
      value !== undefined &&
      value !== null &&
      (typeof value === "string" || typeof value === "number")
    ) {
      return value;
    }
    return props.emptyValueText ?? "--";
  }, [
    normalizedQueryData,
    props.valueField,
    props.value,
    props.emptyValueText,
    templateId,
    hasQueryTemplate,
    hasWorkflow,
    resolvedLinkedChartId,
    isHovering,
    hoverValue,
    linkedChartCurrentValue,
    isTimestampAggregate,
    badgeData.formattedTime,
    queryMeasures,
    computedExpression,
    allDepsLoaded,
    dependencyData,
    allComponents,
    component.id,
  ]);

  useEffect(() => {
    const debug = useDebugStore.getState();
    if (!debug.isEnabled || !debug.planDebugData) return;
    if (isLoading) return;

    const rowKeys =
      queryData?.[0] && typeof queryData[0] === "object"
        ? Object.keys(queryData[0]).slice(0, 20)
        : [];
    const dependencySummary = Object.entries(dependencyData)
      .slice(0, 12)
      .map(([id, rows]) => ({
        id,
        rowCount: Array.isArray(rows) ? rows.length : 0,
        rowKeys:
          rows?.[0] && typeof rows[0] === "object"
            ? Object.keys(rows[0]).slice(0, 20)
            : [],
        error: queryTemplateDependencyErrors[id],
      }));
    const usedFallback =
      extractedValue === null ||
      extractedValue === undefined ||
      extractedValue === "--" ||
      extractedValue === props.emptyValueText;
    let fallbackReason: string | undefined;
    if (usedFallback) {
      if (error) fallbackReason = "query-error";
      else if (
        computedExpression &&
        Object.keys(queryTemplateDependencyErrors).length > 0
      )
        fallbackReason = "computed-dependency-error";
      else if (computedExpression && !allDepsLoaded)
        fallbackReason = "computed-dependencies-not-loaded";
      else if (computedExpression) {
        const unresolvedReference = computedReferenceDiagnostics.find(
          (ref) => ref.status !== "resolved",
        );
        if (unresolvedReference?.status === "no-rows")
          fallbackReason = "computed-dependency-no-rows";
        else if (
          unresolvedReference?.status === "missing-dependency-data"
        )
          fallbackReason = "computed-dependency-data-missing";
        else if (unresolvedReference?.status === "missing-value")
          fallbackReason = "computed-reference-missing-value";
        else if (
          unresolvedReference?.status === "non-numeric-or-invalid-date"
        )
          fallbackReason = "computed-reference-unusable";
        else if (unresolvedReference?.status === "invalid-reference")
          fallbackReason = "computed-reference-invalid";
        else fallbackReason = "computed-evaluation-null";
      } else if (
        (templateId || hasQueryTemplate || hasWorkflow) &&
        (!queryData || queryData.length === 0)
      )
        fallbackReason = "no-query-rows";
      else if (
        templateId &&
        queryData &&
        queryData.length > 0
      )
        fallbackReason = "top-level-template-value-not-extracted";
      else fallbackReason = "no-value";
    }

    const diagnostic = {
      componentId: component.id,
      mode: dataTextMode,
      authUserPresent: !!authUser?.id,
      queryTemplateId: templateId,
      valueField: props.valueField,
      rowCount: queryData?.length ?? 0,
      rowKeys,
      computedExpressionPreview:
        typeof computedExpression === "string"
          ? computedExpression.slice(0, 300)
          : undefined,
      expressionDependencies: expressionDependencyIds,
      dependencySummary,
      referenceSummary: computedReferenceDiagnostics.slice(0, 20),
      allDepsLoaded,
      extractedValueType:
        extractedValue === null ? "null" : typeof extractedValue,
      usedFallback,
      fallbackReason,
      error,
    };
    const key = JSON.stringify(diagnostic);
    if (key === lastRuntimeDiagnosticKeyRef.current) return;
    lastRuntimeDiagnosticKeyRef.current = key;
    debug.recordRuntimeDataTextDiagnostic(diagnostic);
  }, [
    allDepsLoaded,
    authUser?.id,
    component.id,
    computedExpression,
    computedReferenceDiagnostics,
    dataTextMode,
    dependencyData,
    error,
    expressionDependencyIds,
    extractedValue,
    hasQueryTemplate,
    hasWorkflow,
    isLoading,
    props.emptyValueText,
    props.valueField,
    queryData,
    queryTemplateDependencyErrors,
    templateId,
  ]);

  useEffect(() => {
    if (!isLoading && queryData) {
      useComponentDataStore
        .getState()
        .setData(component.id, normalizedQueryData || queryData);
      return;
    }

    if (computedExpression && allDepsLoaded) {
      const resolvedValue =
        extractedValue !== null &&
        extractedValue !== undefined &&
        extractedValue !== "--"
          ? extractedValue
          : null;
      useComponentDataStore
        .getState()
        .setData(component.id, [{ value: resolvedValue }]);
    }
  }, [
    queryData,
    normalizedQueryData,
    isLoading,
    component.id,
    computedExpression,
    allDepsLoaded,
    extractedValue,
  ]);

  useEffect(() => {
    return () => {
      useComponentDataStore.getState().clearData(component.id);
    };
  }, [component.id]);

  // LABEL EXTRACTION
  const extractedLabel = useMemo(() => {
    if (
      (templateId || hasQueryTemplate || hasWorkflow) &&
      queryData &&
      queryData.length > 0 &&
      props.labelField
    ) {
      return (
        (queryData[0][props.labelField] as string) ?? props.label ?? ""
      );
    }
    return props.label ?? "";
  }, [
    queryData,
    props.labelField,
    props.label,
    templateId,
    hasQueryTemplate,
    hasWorkflow,
  ]);

  const linkedChartComponent = useMemo(
    () =>
      allComponents?.find(
        (candidate) => candidate.id === resolvedLinkedChartId,
      ),
    [allComponents, resolvedLinkedChartId],
  );

  const activeValueRow = useMemo<Record<string, unknown> | null>(() => {
    if (isHovering && hoverData?.dataPoint) {
      return hoverData.dataPoint as Record<string, unknown>;
    }

    if (
      resolvedLinkedChartId &&
      chartData &&
      chartData.length > 0
    ) {
      return chartData[chartData.length - 1] as Record<string, unknown>;
    }

    if (
      (templateId || hasQueryTemplate || hasWorkflow) &&
      normalizedQueryData &&
      normalizedQueryData.length > 0
    ) {
      return normalizedQueryData[0] as Record<string, unknown>;
    }

    return null;
  }, [
    chartData,
    hasQueryTemplate,
    hasWorkflow,
    hoverData?.dataPoint,
    isHovering,
    normalizedQueryData,
    resolvedLinkedChartId,
    templateId,
  ]);

  const resolvedTimeZone = useMemo(
    () =>
      resolvePreferredTimeZone(
        props.timeZone,
        props.timezone,
        linkedChartComponent?.properties?.timeZone as string | undefined,
        linkedChartComponent?.properties?.timezone as string | undefined,
        extractTimeZoneFromRecord(
          dataSource?.queryDefinition as
            | Record<string, unknown>
            | undefined,
        ),
        extractTimeZoneFromRecord(activeValueRow),
        getBrowserTimeZone(),
      ),
    [
      activeValueRow,
      dataSource?.queryDefinition,
      linkedChartComponent?.properties,
      props.timeZone,
      props.timezone,
    ],
  );

  // VALUE FORMATTING
  const formattedValue = useMemo(() => {
    if (
      extractedValue === null ||
      extractedValue === undefined ||
      extractedValue === "--"
    ) {
      return extractedValue as string;
    }

    let effectiveFormatter = props.valueFormatter;

    if (!effectiveFormatter && typeof extractedValue === "string") {
      const isoDatePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;
      if (isoDatePattern.test(extractedValue)) {
        effectiveFormatter = "datetime";
      }
    }

    const numericValue =
      typeof extractedValue === "number"
        ? extractedValue
        : typeof extractedValue === "string" &&
            !isNaN(Number(extractedValue))
          ? Number(extractedValue)
          : extractedValue;
    return formatMetricValue(
      numericValue as number | string,
      effectiveFormatter || "number",
      {
        timeZone: resolvedTimeZone,
        decimalPlaces: props.decimalPlaces,
        thousandsSeparator: props.thousandsSeparator,
        currencySymbol: props.currencySymbol,
        currencyCode: props.currencyCode,
      },
    );
  }, [
    extractedValue,
    props.valueFormatter,
    props.decimalPlaces,
    props.thousandsSeparator,
    props.currencySymbol,
    props.currencyCode,
    resolvedTimeZone,
  ]);

  // RESPONSIVE STYLING
  const getResponsiveStyle = useCallback(
    (propName: string, defaultValue: unknown): unknown => {
      if (!viewport || !responsivePosition?.breakpoints) return defaultValue;
      const breakpoint =
        viewport === Viewport.MOBILE
          ? "mobile"
          : viewport === Viewport.TABLET
            ? "tablet"
            : "desktop";
      const bpStyle = responsivePosition.breakpoints[breakpoint]?.style;
      return (
        getBreakpointStyleProperty(
          bpStyle as Record<string, unknown> | undefined,
          propName,
        ) ??
        getBreakpointStyleProperty(
          style as Record<string, unknown> | undefined,
          propName,
        ) ??
        defaultValue
      );
    },
    [viewport, responsivePosition, style],
  );

  const containerStyle: React.CSSProperties = useMemo(() => {
    const labelPos = props.labelPosition || "above";
    const isVertical = labelPos === "above" || labelPos === "below";
    const isReversed = labelPos === "below" || labelPos === "right";

    return {
      display: "flex",
      flexDirection: isVertical
        ? isReversed
          ? "column-reverse"
          : "column"
        : isReversed
          ? "row-reverse"
          : "row",
      alignItems: isVertical ? props.textAlign || "center" : "baseline",
      justifyContent:
        props.textAlign === "center"
          ? "center"
          : props.textAlign === "right"
            ? "flex-end"
            : "flex-start",
      gap: `${props.gap ?? 4}px`,
      width: "100%",
    };
  }, [props.labelPosition, props.textAlign, props.gap]);

  const valueStyle: React.CSSProperties = useMemo(
    () => ({
      fontSize: getResponsiveStyle(
        "fontSize",
        props.fontSize || "24px",
      ) as string,
      fontWeight: getResponsiveStyle(
        "fontWeight",
        props.fontWeight || 600,
      ) as number,
      fontFamily: props.fontFamily,
      color: getResponsiveStyle(
        "color",
        props.color || "inherit",
      ) as string,
      lineHeight: props.lineHeight,
      letterSpacing: props.letterSpacing,
      display: "inline-flex",
      alignItems: "baseline",
      gap: "2px",
    }),
    [
      getResponsiveStyle,
      props.fontSize,
      props.fontWeight,
      props.fontFamily,
      props.color,
      props.lineHeight,
      props.letterSpacing,
    ],
  );

  const labelStyle: React.CSSProperties = useMemo(
    () => ({
      fontSize: props.labelFontSize || "12px",
      fontWeight: props.labelFontWeight || 400,
      color: props.labelColor || "#6B7280",
      textTransform: "uppercase" as const,
      letterSpacing: "0.05em",
    }),
    [props.labelFontSize, props.labelFontWeight, props.labelColor],
  );

  const unitGapPx = props.unitGap ?? 2;
  const unitStyle: React.CSSProperties = useMemo(
    () => ({
      fontSize: props.unitFontSize || "0.7em",
      color: props.unitColor || "inherit",
      marginLeft: `${unitGapPx}px`,
    }),
    [props.unitFontSize, props.unitColor, unitGapPx],
  );

  // CONDITIONAL COLOR COMPUTATION
  const computedValueColor = useMemo(() => {
    if (!props.useConditionalColor) {
      return getResponsiveStyle(
        "color",
        props.color || "inherit",
      ) as string;
    }

    let numericValue: number | null = null;

    if (typeof extractedValue === "number") {
      numericValue = extractedValue;
    } else if (typeof extractedValue === "string") {
      const stripped = extractedValue.replace(/[$€£¥,\s%]/g, "");
      const parsed = parseFloat(stripped);
      if (!isNaN(parsed)) {
        numericValue = parsed;
      }
    }

    if (numericValue === null) {
      return getResponsiveStyle(
        "color",
        props.color || "inherit",
      ) as string;
    }

    const positiveThreshold = props.positiveThreshold ?? 0;
    const negativeThreshold = props.negativeThreshold ?? 0;

    if (numericValue > positiveThreshold) {
      return props.positiveColor || "#059669";
    } else if (numericValue < negativeThreshold) {
      return props.negativeColor || "#DC2626";
    } else {
      return props.zeroColor || "#6B7280";
    }
  }, [
    props.useConditionalColor,
    props.color,
    props.positiveColor,
    props.negativeColor,
    props.zeroColor,
    props.positiveThreshold,
    props.negativeThreshold,
    extractedValue,
    getResponsiveStyle,
  ]);

  // VALUE ANIMATION
  const previousValueRef = useRef<string | number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [displayValue, setDisplayValue] = useState<string>(formattedValue);
  const animationFrameRef = useRef<number | null>(null);

  useEffect(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }

    if (!props.animateValueChange || previousValueRef.current === null) {
      previousValueRef.current = extractedValue;
      setDisplayValue(formattedValue);
      return;
    }

    const animationType = props.animationType || "fade";
    const duration = Math.max(
      100,
      Math.min(2000, props.animationDuration || 300),
    );

    if (animationType === "count") {
      const oldNumeric =
        typeof previousValueRef.current === "number"
          ? previousValueRef.current
          : parseFloat(
              String(previousValueRef.current).replace(
                /[$€£¥,\s%]/g,
                "",
              ),
            );
      const newNumeric =
        typeof extractedValue === "number"
          ? extractedValue
          : parseFloat(
              String(extractedValue).replace(/[$€£¥,\s%]/g, ""),
            );

      if (!isNaN(oldNumeric) && !isNaN(newNumeric)) {
        const startTime = performance.now();
        const diff = newNumeric - oldNumeric;

        const animate = (currentTime: number) => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);
          const easeProgress = 1 - Math.pow(1 - progress, 3);
          const currentValue = oldNumeric + diff * easeProgress;
          const formatted = formatMetricValue(
            currentValue,
            props.valueFormatter || "number",
            {
              timeZone: resolvedTimeZone,
              decimalPlaces: props.decimalPlaces,
              thousandsSeparator: props.thousandsSeparator,
              currencySymbol: props.currencySymbol,
              currencyCode: props.currencyCode,
            },
          );
          setDisplayValue(formatted);

          if (progress < 1) {
            animationFrameRef.current = requestAnimationFrame(animate);
          } else {
            setDisplayValue(formattedValue);
            previousValueRef.current = extractedValue;
          }
        };

        animationFrameRef.current = requestAnimationFrame(animate);
      } else {
        setDisplayValue(formattedValue);
        previousValueRef.current = extractedValue;
      }
    } else {
      setIsAnimating(true);
      setDisplayValue(formattedValue);

      const timeoutId = setTimeout(() => {
        setIsAnimating(false);
        previousValueRef.current = extractedValue;
      }, duration);

      return () => clearTimeout(timeoutId);
    }

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [
    extractedValue,
    formattedValue,
    props.animateValueChange,
    props.animationType,
    props.animationDuration,
    props.valueFormatter,
    props.decimalPlaces,
    props.thousandsSeparator,
    props.currencySymbol,
    props.currencyCode,
    resolvedTimeZone,
  ]);

  const animationStyles: React.CSSProperties = useMemo((): React.CSSProperties => {
    if (
      !props.animateValueChange ||
      props.animationType === "count" ||
      props.animationType === "none"
    ) {
      return {};
    }

    const duration = Math.max(
      100,
      Math.min(2000, props.animationDuration || 300),
    );
    const easing = props.animationEasing || "ease-out";
    const animationType = props.animationType || "fade";
    const baseTransition = `all ${duration}ms ${easing}`;

    switch (animationType) {
      case "fade":
        return {
          transition: baseTransition,
          opacity: isAnimating ? 0.3 : 1,
        };
      case "slide":
        return {
          transition: baseTransition,
          transform: isAnimating ? "translateY(-4px)" : "translateY(0)",
          opacity: isAnimating ? 0.5 : 1,
        };
      case "scale":
        return {
          transition: baseTransition,
          transform: isAnimating ? "scale(1.1)" : "scale(1)",
        };
      default:
        return {};
    }
  }, [
    props.animateValueChange,
    props.animationType,
    props.animationDuration,
    props.animationEasing,
    isAnimating,
  ]);

  const valueStyleWithColor: React.CSSProperties = useMemo(
    () => ({
      ...valueStyle,
      color: computedValueColor,
      ...animationStyles,
    }),
    [valueStyle, computedValueColor, animationStyles],
  );

  // LOADING STATE
  const isWaitingForDeps = !!(
    computedExpression &&
    expressionDependencyIds.length > 0 &&
    !allDepsLoaded
  );
  if ((isLoading || isWaitingForDeps) && props.showLoadingState !== false) {
    return (
      <div
        className="w-full"
        data-testid="data-text-loading"
      >
        <DataTextLoadingSkeleton />
      </div>
    );
  }

  // ERROR STATE
  if (error && props.showErrorState !== false && !computedExpression) {
    return (
      <div
        className="flex flex-col items-center justify-center gap-2 w-full text-center"
        data-testid="data-text-error"
      >
        <div className="flex items-center gap-2 text-red-500">
          <AlertCircle className="w-4 h-4" />
          <span className="text-sm">
            {props.errorText || "Failed to load"}
          </span>
        </div>
        <button
          onClick={() => refetch()}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 transition-colors"
        >
          <RefreshCw className="w-3 h-3" />
          Retry
        </button>
      </div>
    );
  }

  // MAIN RENDER
  const showLabel = props.showLabel !== false && extractedLabel;

  return (
    <div style={containerStyle} data-testid="data-text">
      {showLabel && (
        <span style={labelStyle} data-testid="data-text-label">
          {extractedLabel}
        </span>
      )}

      <span style={valueStyleWithColor} data-testid="data-text-value">
        {typeof props.prefix === "string" && (
          <span>{props.prefix}</span>
        )}
        <span>{displayValue}</span>
        {typeof props.unit === "string" &&
          !(
            typeof displayValue === "string" &&
            displayValue.endsWith(props.unit)
          ) && <span style={unitStyle}>{props.unit}</span>}
        {typeof props.suffix === "string" &&
          (!props.unit ||
            props.suffix.trim().toLowerCase() !==
              String(props.unit).trim().toLowerCase()) && (
            <span
              style={
                !props.unit
                  ? { marginLeft: `${unitGapPx}px` }
                  : undefined
              }
            >
              {props.suffix}
            </span>
          )}
      </span>
    </div>
  );
};

DataTextRenderer.displayName = "DataTextRenderer";

// ─────────────────────────────────────────────────────────────────────────────
// Public dispatcher
// ─────────────────────────────────────────────────────────────────────────────

export function TextRenderers({
  component,
  viewport,
  allComponents,
}: TextRendererProps): JSX.Element {
  if (component.type === ComponentType.DATA_TEXT) {
    return (
      <DataTextRenderer
        component={component}
        viewport={viewport}
        allComponents={allComponents}
      />
    );
  }
  return (
    <TextRenderer
      component={component}
      viewport={viewport}
      allComponents={allComponents}
    />
  );
}
